<template>
  <div class="my-pills-list content-top-bottom">
    <header class="topbar">
      <div v-if="!hasOptionsSelected" class="container container-flex flex-align-center">
        <h1
          class="topbar__title"
          :class="{
            'topbar__title--search': searching,
          }"
        >
          <template v-if="searching">
            <img @click="searchQuery = ''" src="~@/assets/icons/icon-prev-black.svg" class="topbar__back-icon" />
            {{ $t("common.back") }}
          </template>
          <template v-else>Your list of pills</template>
        </h1>
        <form class="topbar__form">
          <input
            type="text"
            id="search-pill-input"
            class="topbar__input"
            :placeholder="`${$t('page.order.search.placeholder')}`"
            v-model="searchQuery"
            @keydown.enter.prevent
          />
          <v-button @click="$refs.xlsImportInput.click()" class="btn btn--icon topbar__import">
            <i class="icon-folder"></i>
            Import .xls
            <input
              ref="xlsImportInput"
              type="file"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              @change="uploadXlsFile($event)"
            />
          </v-button>
        </form>
        <v-button
          v-if="!searching"
          class="ml-auto no-border button--white-border"
          @click="startExcelDownload"
          :loading="downloadExcellInProgress"
        >
          <i class="icon-export mr-10"></i>
          {{ $t("page.order.confirmation.exportlist") }}
        </v-button>
        <v-button primary @click="$router.push('add-pill/')" v-if="!searching">
          <img src="~@/assets/icons/icon-plus-white.svg" class="icon-left" />
            {{$t("page.order.add.new.pill")}}
        </v-button>
        <!--<a
          v-if="!searching"
          href="#"
          id="refresh-button"
          class="btn btn--icon btn--bg-orange btn--refresh topbar__check-data"
          @click.prevent="loadData(true)"
        >
          <i class="icon-refresh"></i>
          {{ $t("page.pills.search.refresh") }}
        </a>-->
        <a
          v-if="!searching && pillAmountForCheckData"
          href="#"
          id="check-data-button"
          class="btn btn--icon btn--bg-orange topbar__check-data"
          @click.prevent="$router.push({ name: 'checkData' })"
        >
          <i class="icon-check-data"></i>
          Check data ({{ pillAmountForCheckData }})
        </a>
      </div>
      <div v-else class="container flex-center-between">
        <div class="d-flex topbar__selected-text">
          <span class="topbar__selected-text__icon" @click="clearItemsSelection()">x</span>
          {{ $t("common.selected") }} ({{ selectedWorkListItems.length || selectedPills.length }})
        </div>

        <div class="counter-box">
          <span
            class="counter-box__minus"
            :class="{
              'counter-box__minus--disabled': computedAmountOfCanisters === 0,
              'no-pointer-events': changeInProgress,
            }"
            @click="decreaseCanister()"
          >
            -
          </span>
          <span class="counter-box__number">
            {{ hasSameAmountOfCanisters ? computedAmountOfCanisters : "MIXED" }}
          </span>
          <span class="counter-box__plus" :class="changeInProgress && 'no-pointer-events'" @click="increaseCanister()">
            +
          </span>
        </div>
      </div>
    </header>
    <div class="list-wrapper content-center">
      <div v-if="!loading" id="my-pills-list-container" class="container animate-in">
        <v-work-list
          v-if="!searching"
          :statuses="statuses"
          :clear-options="!hasOptionsSelected"
          :show-check-data-popup.sync="showCheckDataPopup"
          @items-selected="onItemsSelected($event)"
        />
        <v-pills-list
          v-else
          :pills="pills"
          :clear-options="!hasOptionsSelected"
          :pills-to-update="Array.from(pillsUpdated)"
          :pills-to-add="Array.from(pillsAdded)"
          :pills-to-remove="Array.from(pillsRemoved)"
          :search-query="searchQuery"
          @pills-selected="onPillsSelected($event)"
          @success="searchQuery = ''"
        />

        <v-pill-remove-popup
          @close="showRemovePillsPopup = false"
          @delete="removePills()"
          v-if="showRemovePillsPopup"
          pill="many pills"
        />

        <v-check-data-popup
          v-if="showCheckDataPopup"
          :number="pillAmountForCheckData"
          @navigation="internalNavigation = true"
          @close="showCheckDataPopup = false"
        />
      </div>
      <v-loader v-else />
      <v-pills-upload-status-popup
        v-if="showUploadPopup"
        :title="fileUploadTitle"
        :progress="fileUploadProgress"
        @close="closeFileUploadPopup()"
      />
      <v-error-popup
        v-if="showErrorPopup"
        :title="errorTitle"
        :description="errorDescription"
        @close="closeErrorPopup()"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { VLoader, VButton } from "shared";
import * as WorkListStatusesDAO from "@/dao/worklist-statuses-dao";
import * as WorklistsDAO from "@/dao/worklists-dao";
import * as DownloadUtils from "@/utils/DownloadUtils";
import * as PromiseUtils from "@/utils/PromiseUtils";
import VPillRemovePopup from "@/components/pills/v-pill-remove-popup";
import VPillsUploadStatusPopup from "@/components/pills/v-pills-upload-status-popup";
import VPillsList from "@/components/pills/v-pills-list";
import VCheckDataPopup from "@/components/work-list/v-check-data-popup";
import VWorkList from "@/components/work-list/v-work-list";
import VErrorPopup from "@/components/v-error-popup"

export default {
  name: "PMyPillsList",
  components: {
    VLoader,
    VButton,
    VWorkList,
    VPillsList,
    VPillRemovePopup,
    VCheckDataPopup,
    VPillsUploadStatusPopup,
    VErrorPopup
  },
  props: {
    location: {
      type: String,
      default: null,
    },
    medKeyType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      statuses: null,
      loading: true,
      searchQuery: this.$route.query.search || "",
      selectedWorkListItems: [],
      selectedPills: [],
      showRemovePillsPopup: false,
      showCheckDataPopup: false,
      pillsAdded: new Set(),
      pillsUpdated: new Set(),
      pillsRemoved: new Set(),
      xlsFile: null,
      requestId: null,
      fileUploadStatus: null,
      fileUploadProgress: 0,
      fileUploadTitle: "page.pills.upload.status.uploaded",
      showUploadPopup: false,
      allowCloseUploadPopup: false,
      searchTimer: null,
      downloadExcellInProgress: false,
      internalNavigation: false,
      searching: !!this.$route.query.search,
      changeInProgress: false,
      errorTitle: "",
      errorDescription: "",
      showErrorPopup: false,
    };
  },

  watch: {
    async searchQuery(searchQuery) {
      clearTimeout(this.searchTimer);

      this.searchTimer = setTimeout(async () => {
        const search = searchQuery.length > 2 ? searchQuery : undefined;
        if (search !== this.$route.query.search) {
          await this.$router.push({ query: { search } });
        }
        await this.loadData();
      }, 750);
    },
    "$route.query.search": function (search) {
      this.searchQuery = search || "";
    },
  },

  async created() {
    this.loading = true;
    this.statuses = await WorkListStatusesDAO.getStatuses();
    await this.loadData(true);
    this.loading = false;
  },

  methods: {
    ...mapActions({
      getMyWorkLists: "workLists/getMyWorkLists",
      getMyWorkList: "workLists/getMyWorkListsById",
      increaseByIdsList: "workLists/increaseByIdsList",
      changePillsValueByIdsList: "pills/changePillsValueByIdsList",
      addPillsValueByIdsList: "pills/addPillsValueByIdsList",
      getPills: "pills/getPills",
      uploadXlsPillsFile: "pills/uploadXlsPillsFile",
      getUploadStatus: "pills/getUploadStatus",
    }),

    async loadData(force) {
      if (this.searchQuery.length > 2) {
        this.loading = true;
        this.searching = true;
        await this.getPills({ searchQuery: this.searchQuery, listId: this.workListId });
      } else if (force || this.searching) {
        this.loading = true;
        this.searching = false;
        await this.getMyWorkLists({
          location: this.location,
          medKeyType: this.medKeyType,
        });
        await this.getMyWorkList();
      }
      this.loading = false;
    },
    onItemsSelected(workListItems) {
      this.selectedWorkListItems = workListItems;
      this.selectedPills = [];
    },
    clearItemsSelection() {
      this.selectedWorkListItems = [];
      this.selectedPills = [];
    },
    async increaseCanister() {
      if (this.changeInProgress) return;
      this.changeInProgress = true;
      if (this.hasSameAmountOfCanisters) {
        await this.changeCanisterValue(this.computedAmountOfCanisters + 1);
      } else {
        const amountOfCanisters = this.selectedWorkListItems.length
          ? Math.max(...this.selectedWorkListItems.map((o) => o.amountOfCanisters), 0)
          : Math.max(...this.selectedPills.map((o) => o.amountOfCanisters), 0);
        await this.changeCanisterValue(amountOfCanisters);
      }
      this.changeInProgress = false;
    },
    async decreaseCanister() {
      if (this.changeInProgress) return;
      this.changeInProgress = true;
      if (this.hasSameAmountOfCanisters) {
        if (this.computedAmountOfCanisters !== 1) {
          await this.changeCanisterValue(this.computedAmountOfCanisters - 1);
        } else if (!this.searchQuery.length) {
          this.showRemovePillsPopup = true;
        } else {
          await this.changeCanisterValue(0);
        }
      } else {
        const amountOfCanisters = this.selectedWorkListItems.length
          ? Math.min(...this.selectedWorkListItems.map((o) => o.amountOfCanisters))
          : Math.min(...this.selectedPills.map((o) => o.amountOfCanisters));
        await this.changeCanisterValue(amountOfCanisters);
      }
      this.changeInProgress = false;
    },
    async removePills() {
      this.showRemovePillsPopup = false;
      await this.changeCanisterValue(0);
      this.clearItemsSelection();
    },
    async changeCanisterValue(amountOfCanisters) {
      if (this.selectedWorkListItems.length) {
        await this.increaseByIdsList({
          listId: this.workListId,
          idsList: this.selectedWorkListItems,
          value: amountOfCanisters,
          query: this.$route.query.search,
        });
      } else {
        this.setPillsToModifyType(amountOfCanisters);
        this.clearModifiedPills();
        await this.updatePillsToModify(amountOfCanisters);
      }
    },
    clearModifiedPills() {
      setTimeout(() => {
        this.pillsUpdated = new Set();
        this.pillsAdded = new Set();
        this.pillsRemoved = new Set();
      }, 3000);
    },
    setPillsToModifyType(amountOfCanisters) {
      let pills = Object.assign([], this.selectedPills);
      if (amountOfCanisters === 0) {
        pills = pills.filter((p) => p.amountOfCanisters !== 0);
      }
      pills.forEach((p) => {
        if (p.amountOfCanisters === 0) {
          this.pillsAdded.add(p);
        } else if (amountOfCanisters === 0) {
          this.pillsRemoved.add(p);
        } else {
          this.pillsUpdated.add(p);
        }
      });
    },
    async updatePillsToModify(amountOfCanisters) {
      if (this.pillsAdded.size !== 0) {
        const pillsAdded = Array.from(this.pillsAdded);
        const responseItems = await this.addPillsValueByIdsList({
          listId: this.workListId,
          pillsIdsList: Array.from(this.pillsAdded),
          value: amountOfCanisters,
          query: this.$route.query.search,
        });
        for (let i = 0; i < pillsAdded.length; i++) {
          pillsAdded[i].worklistItemId = responseItems[i].worklistItemId;
        }
      }
      if (this.pillsUpdated.size !== 0 || this.pillsRemoved.size !== 0) {
        await this.changePillsValueByIdsList({
          listId: this.workListId,
          pillsIdsList: [...Array.from(this.pillsUpdated), ...Array.from(this.pillsRemoved)],
          value: amountOfCanisters,
          query: this.$route.query.search,
        });
      }
    },
    onPillsSelected(pills) {
      this.selectedPills = pills;
      this.selectedWorkListItems = [];
    },
    async uploadXlsFile($event) {
      this.resetFileUpload();
      this.xlsFile = $event.target.files[0];
      let formData = new FormData();
      formData.append("file", this.xlsFile);
      try {
        this.requestId = await this.uploadXlsPillsFile({
          listId: this.workListId,
          file: formData,
        });
        $event.target.value = null;
        this.xlsFile = null;
        this.allowCloseUploadPopup = false;
        this.showUploadPopup = true;
        this.checkUploadStatus();
      } catch (e) {
        $event.target.value = null;
        this.xlsFile = null;
        this.allowCloseUploadPopup = true;
      }
    },
    checkUploadStatus() {
      const statusInterval = setInterval(async () => {
        try {
          const {
            status,
            data: { progress, statusTranslationKey },
          } = await this.getUploadStatus({
            listId: this.workListId,
            requestId: this.requestId,
          });
          this.fileUploadProgress = progress;
          this.fileUploadStatus = status;
          this.fileUploadTitle = statusTranslationKey;
          if (this.fileUploadStatus === 200) {
            clearInterval(statusInterval);
            this.allowCloseUploadPopup = true;
            setTimeout(async () => {
              this.showUploadPopup = false;
              this.resetFileUpload();
              this.loading = true;
              await this.getMyWorkList();
              this.loading = false;
            }, 2000);
          }
        } catch (error) {
          const {
            status,
            data: { statusTranslationKey },
          } = error.response;
          clearInterval(statusInterval);
          this.resetFileUpload();
          if (status === 422) {
            this.fileUploadTitle = statusTranslationKey;
          }
          this.allowCloseUploadPopup = true;
        }
      }, 2000);
    },
    resetFileUpload() {
      this.fileUploadProgress = 0;
      this.fileUploadStatus = null;
      this.fileUploadTitle = "page.pills.upload.status.uploaded";
    },
    closeFileUploadPopup() {
      if (this.allowCloseUploadPopup) {
        this.showUploadPopup = false;
      }
    },
    async startExcelDownload() {
      this.downloadExcellInProgress = true;
      const { requestId } = await WorklistsDAO.startExcelDownload(this.workListId);

      let downloadedExcelFile = await WorklistsDAO.getDownloadedExcel(this.workListId, requestId);
      while (downloadedExcelFile.status === "inprogress") {
        await PromiseUtils.delay(2000); // eslint-disable-line
        downloadedExcelFile = await WorklistsDAO.getDownloadedExcel(this.workListId, requestId);
      }

      if (downloadedExcelFile.status === "success") {
        const url = downloadedExcelFile.url;
        await DownloadUtils.downloadFile(url);
        this.downloadExcellInProgress = false;
      }
      else {
        this.downloadExcellInProgress = false;
        this.errorDescription = this.$t("page.pills.export.failed")
        this.showErrorPopup = true;
      }
    },
    closeErrorPopup() {
      this.showErrorPopup = false
    },
  },
  computed: {
    ...mapGetters({
      workListItems: "workLists/currentWorkList",
      workListId: "workLists/currentWorkListId",
      pillAmountForCheckData: "workLists/pillAmountForCheckData",
      pills: "pills/pills",
      user: "user/userDetails",
      currentMedKeyTypeName: "medKey/currentMedKeyTypeName",
    }),
    hasSameAmountOfCanisters() {
      if (this.searching) {
        return this.selectedPills.every((val, i, arr) => val.amountOfCanisters === arr[0].amountOfCanisters);
      } else {
        return this.selectedWorkListItems.every((val, i, arr) => val.amountOfCanisters === arr[0].amountOfCanisters);
      }
    },
    hasOptionsSelected() {
      return this.searching ? !!this.selectedPills.length : !!this.selectedWorkListItems.length;
    },
    computedAmountOfCanisters() {
      return this.searching ? this.selectedPills[0].amountOfCanisters : this.selectedWorkListItems[0].amountOfCanisters;
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.showCheckDataPopup) {
      this.showCheckDataPopup = false;
      next(this.internalNavigation);
    } else {
      next();
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/variables";

.my-pills-list {
  position: relative;
  overflow: hidden;
}

.topbar {
  min-height: 104px;
  background: #fdfdfd;
  box-shadow: 0 38px 46px -48px rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  &__title {
    font-size: 24px;
    line-height: 26px;
    letter-spacing: 0.8px;
    color: #333333;
    position: relative;
    transition: all 0.3s;
    &--search {
      padding-left: 25px;
    }
  }

  &__back-icon {
    margin-right: 10px;
    position: absolute;
    left: 0;
    top: 5px;
    cursor: pointer;
    animation: fade-left 0.3s forwards;
  }

  &__form {
    background: url("~@/assets/icons/icon-search.svg") #fdfdfd 18px center no-repeat;
    border: 1px solid #ececec;
    border-radius: 100px;
    margin: 0 0 0 39px;
    min-width: 470px;
    display: flex;
    align-items: center;
    padding: 0 21px 0 0;
  }
  &__input {
    background: none;
    padding: 0 0 0 46px;
    border: none;
    height: 40px;
    font-size: 13px;
    line-height: 14px;
    width: 330px;
    outline: 0;
    &::placeholder {
      color: #999999;
      opacity: 1;
    }
  }
  &__import {
    margin-left: auto;
    text-transform: none;
    letter-spacing: 0;
    padding: 0;
    background: none;
    outline: none;
  }
  .counter-box {
    margin-right: 60px;
    margin-left: auto;
    display: flex;
    align-items: center;
  }
  &__buttons {
    .button {
      padding: 0 0 0 24px;
      background: none;
      color: #004593;
      display: inline-block;
      white-space: nowrap;
      margin: 0 0 0 9px;
      vertical-align: middle;
      min-width: 192px;
      img {
        display: inline-block;
        vertical-align: middle;
      }
    }
  }
  &__selected-text {
    font-size: 24px;
    opacity: 0;
    animation: fade 0.5s forwards;
    span {
      margin: 8px 16px 0 7px;
      background: url("~@/assets/icons/icon-cancel-black.svg");
      text-indent: -99999px;
      display: inline-block;
      width: 14px;
      height: 14px;
      vertical-align: middle;
      cursor: pointer;
    }
  }
}

.btn {
  &--refresh {
    margin-right: 10px;
  }
}

.list {
  background: $color-faint-grey;
}

.list-wrapper {
  padding-top: 30px;
  &::v-deep .counter-box {
    margin-left: 10px;
  }
}
.animate-in {
  opacity: 0;
  animation: fade 0.5s forwards;
}
@keyframes fade {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@keyframes fade-left {
  0% {
    opacity: 0;
    margin-left: -21px;
  }
  100% {
    opacity: 1;
    margin-left: 0;
  }
}
</style>
